import { z } from 'zod';
import { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CasFormInput from '@cas/components/inputs/cas-form-input/CasFormInput';
import { ModelName } from '@casops/store/api';
import CasFormDropdown from '@cas/components/inputs/cas-form-dropdown/CasFormDropdown';
import ModelForm from '@casops/components/layout/model-form/ModelForm';
import useModelForm, { CanSubmitProps } from '@casops/components/layout/model-form/useModelForm';
import {
  CreateGeofenceHoldTimeModel,
  EditGeofenceHoldTimeModel,
  GeofenceHoldTimeFormModel,
  GeofenceHoldTimeModel,
  useCreateGeofenceHoldTimeMutation,
  useEditGeofenceHoldTimeMutation,
} from '@casops/store/apis/geofenceHoldTimesApi';
import { useGreaterThanOptions } from './useGreaterThanOptions';
import { ServerErrorFields } from '@dmc/ui';

type DataModel = GeofenceHoldTimeModel;
type FormModel = GeofenceHoldTimeFormModel;
type CreateModel = CreateGeofenceHoldTimeModel;
type EditModel = EditGeofenceHoldTimeModel;

const modelName = ModelName.GeofenceHoldTime;

const defaultValues: FormModel = {
  isGreater: false,
  time: 1,
  desc: '',
};

// Allow an untouched (!isAnyFieldDirty) Create form to be submitted since all default values are valid.
const canSubmitCallback = ({
  isSubmitting,
  isAnyFieldDirty,
  isValid,
  editMode,
}: CanSubmitProps): boolean => {
  return !isSubmitting && isValid && (isAnyFieldDirty || !editMode);
};

// Note: isGreater does not have a custom validation response from the API.
const serverErrorFields: ServerErrorFields<FormModel> = {
  time: 'Time',
  desc: 'Description',
};

const MIN_TIME = 1;
const MAX_TIME = 9999;
const DESCRIPTION_MAX = 255;

interface HoldTimeFormProps {
  item?: DataModel;
}

export const HoldTimeForm = ({ item }: HoldTimeFormProps): ReactElement => {
  const { t } = useTranslation();

  const schema = useMemo(() => {
    const timeOutOfRangeMessage = t('Validation.TimeOutOfRange', {
      min: MIN_TIME,
      max: MAX_TIME,
    });

    return z.object({
      isGreater: z.boolean(),
      time: z
        .number()
        .min(MIN_TIME, { message: timeOutOfRangeMessage })
        .max(MAX_TIME, { message: timeOutOfRangeMessage })
        .int({ message: t('Validation.ValueMustBeInteger') }),
      desc: z.string().max(DESCRIPTION_MAX, {
        message: t('Validation.DescriptionTooLong', { max: DESCRIPTION_MAX }),
      }),
    });
  }, [t]);

  const [triggerCreateItem] = useCreateGeofenceHoldTimeMutation();
  const [triggerEditItem] = useEditGeofenceHoldTimeMutation();

  const { canSubmit, onSubmit, errorMessage, control } = useModelForm<
    DataModel,
    FormModel,
    CreateModel,
    EditModel
  >({
    modelName,
    defaultValues,
    item,
    schema,
    triggerCreateItem,
    triggerEditItem,
    serverErrorFields,
    canSubmitCallback,
  });

  const greaterThanOptions = useGreaterThanOptions();

  return (
    <ModelForm
      canSubmit={canSubmit}
      onSubmit={onSubmit}
      errorMessage={errorMessage}
      control={control}
    >
      <>
        <CasFormDropdown
          key="isGreater"
          control={control}
          name="isGreater"
          label={t('Fields.IsGreater')}
          items={greaterThanOptions}
        />

        <CasFormInput
          key="time"
          control={control}
          name="time"
          label={t('Fields.Time')}
          type="number"
        />

        <CasFormInput
          key="desc"
          control={control}
          name="desc"
          label={t('Fields.Description')}
          element="textarea"
          minHeight="100px"
        />
      </>
    </ModelForm>
  );
};

export default HoldTimeForm;
